import { startE2eAwareQuickFillApply } from '../config/e2eQuickFillMixedScopeFixture';
import { webLog } from '../api/grpc/webLogger';
import {
  buildBatchCellResults,
  buildElementBatchCellResults,
  emitHostQuickFillApplyProgress,
} from './quickFillApplyResults';
import type {
  CellUpdateResult,
  PreparedElementUpdate,
  PreparedProjectTypeUpdate,
  QuickFillBindingScope,
  SaveChangesOptions,
} from './quickFillRevitTypes';
import { emitProgress } from './quickFillRevitTypes';

type QuickFillApplyBatchResult = NonNullable<
  Awaited<ReturnType<typeof startE2eAwareQuickFillApply>>['familyTypeResult']
>;

function asBatchResponse(
  result: QuickFillApplyBatchResult | undefined,
  updateCount: number
): { success: boolean; error?: string; data?: unknown } {
  return {
    success: result?.success ?? updateCount === 0,
    error: result?.error,
    data: {
      totalUpdates: result?.totalUpdates ?? updateCount,
      successCount: result?.successCount ?? (result?.success === false ? 0 : updateCount),
      errorCount: result?.errorCount ?? (result?.success === false ? updateCount : 0),
      errors: result?.errors ?? [],
    },
  };
}

export function toGrpcFamilyTypeUpdate(update: PreparedProjectTypeUpdate): {
  familyType: string;
  parameterName: string;
  value: string;
  familyTypeId?: string;
  targetElementIds?: string[];
  bindingKind?: QuickFillBindingScope;
} {
  const targetElementIds = (update.TargetElementIds ?? [])
    .map((id) => id.trim())
    .filter(Boolean);
  return {
    familyType: update.FamilyType,
    parameterName: update.ParameterName,
    value: update.Value,
    ...(update.FamilyTypeId ? { familyTypeId: update.FamilyTypeId } : {}),
    ...(targetElementIds.length > 0 ? { targetElementIds } : {}),
    ...(update.BindingScope === 'unknown' ? {} : { bindingKind: update.BindingScope }),
  };
}

interface PreparedRevitBatchOutcome {
  results: CellUpdateResult[];
  modelVersion?: number;
}

async function executePreparedRevitOperation(
  projectTypeUpdates: PreparedProjectTypeUpdate[],
  elementUpdates: PreparedElementUpdate[],
  options?: SaveChangesOptions
): Promise<PreparedRevitBatchOutcome> {
  const buildFailureResults = (error: string): CellUpdateResult[] => [
    ...projectTypeUpdates.map((update) => ({
      familyType: update.rowLabel,
      parameter: update.ParameterName,
      value: update.Value,
      success: false,
      error,
    })),
    ...elementUpdates.map((update) => ({
      familyType: update.rowLabel,
      parameter: update.parameterName,
      value: update.value,
      success: false,
      error,
    })),
  ];

  try {
    const grpcProjectUpdates = projectTypeUpdates.map(toGrpcFamilyTypeUpdate);
    const grpcElementUpdates = elementUpdates.map((update) => ({
      elementId: update.elementId,
      parameterName: update.parameterName,
      value: update.value,
    }));
    webLog.info(
      'quickFillRevitService',
      `apply payload projectTypeUpdates=${grpcProjectUpdates.length}, elementUpdates=${grpcElementUpdates.length}, scopedTypeUpdates=${grpcProjectUpdates.filter((update) => update.bindingKind === 'type').length}, scopedInstanceUpdates=${grpcProjectUpdates.filter((update) => update.bindingKind === 'instance').length}`
    );
    const applyResult = await startE2eAwareQuickFillApply(
      grpcProjectUpdates,
      grpcElementUpdates,
      { onProgress: (progress) => emitHostQuickFillApplyProgress(options, progress) }
    );
    webLog.info(
      'quickFillRevitService',
      `apply result success=${applyResult.success}, modelVersion=${applyResult.modelVersion ?? 'unknown'}, familyTypeSuccessCount=${applyResult.familyTypeResult?.successCount ?? 0}, familyTypeErrorCount=${applyResult.familyTypeResult?.errorCount ?? 0}, elementSuccessCount=${applyResult.elementResult?.successCount ?? 0}, elementErrorCount=${applyResult.elementResult?.errorCount ?? 0}`
    );

    if (!applyResult.success && !applyResult.familyTypeResult && !applyResult.elementResult) {
      return {
        results: buildFailureResults(applyResult.error || 'Quick Fill apply failed before Revit returned row results'),
      };
    }
    if (!applyResult.familyTypeResult && !applyResult.elementResult) {
      return { results: buildFailureResults('Quick Fill apply returned no row results') };
    }
    return {
      results: [
        ...buildBatchCellResults(
          projectTypeUpdates,
          asBatchResponse(applyResult.familyTypeResult, projectTypeUpdates.length)
        ),
        ...buildElementBatchCellResults(
          elementUpdates,
          asBatchResponse(applyResult.elementResult, elementUpdates.length)
        ),
      ],
      modelVersion: applyResult.modelVersion,
    };
  } catch (error) {
    return {
      results: buildFailureResults(error instanceof Error ? error.message : 'Quick Fill apply failed'),
    };
  }
}

export async function executePreparedRevitBatches(
  projectTypeUpdates: PreparedProjectTypeUpdate[],
  elementUpdates: PreparedElementUpdate[],
  skippedResults: CellUpdateResult[] = [],
  options?: SaveChangesOptions
): Promise<PreparedRevitBatchOutcome> {
  const backendUpdateCount = projectTypeUpdates.length + elementUpdates.length;
  const totalUpdates = backendUpdateCount + skippedResults.length;
  if (backendUpdateCount === 0) {
    emitProgress(options, {
      phase: 'completed',
      message: skippedResults.length > 0
        ? 'No project-writable Quick Fill changes to apply to Revit'
        : 'No Quick Fill changes to apply to Revit',
      current: skippedResults.length,
      total: totalUpdates,
    });
    return { results: [...skippedResults] };
  }

  const { results, modelVersion } = await executePreparedRevitOperation(
    projectTypeUpdates,
    elementUpdates,
    options
  );
  emitProgress(options, {
    phase: 'completed',
    message: 'Revit apply completed',
    current: totalUpdates,
    total: totalUpdates,
    resultReady: true,
  });
  return { results: [...results, ...skippedResults], modelVersion };
}
